import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Star, Copy, Check, ExternalLink, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_OPTIONS = [
  { id: 'campaign', label: 'Favorite Campaigns', help: 'Shows one hero image or video per favorited campaign.' },
  { id: 'ad',       label: 'Favorite Ads',       help: 'Individual ads you starred inside campaigns.' },
  { id: 'gallery',  label: 'Favorite Gallery',   help: 'Images and videos you starred in My Gallery.' },
];

const PublicReelPage = () => {
  const [reel, setReel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/reel/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const r = res.data?.reel;
      setReel(r);
      setSlugInput(r?.slug || '');
      setTitle(r?.title || '');
      setTagline(r?.tagline || '');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load reel');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.patch(`${API}/reel/mine`, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setReel(res.data?.reel);
      setSlugInput(res.data?.reel?.slug || '');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = reel ? `${window.location.origin}/reel/${reel.slug}` : '';

  const toggleKind = (kind) => {
    const current = new Set(reel?.include_kinds || []);
    if (current.has(kind)) current.delete(kind); else current.add(kind);
    patch({ include_kinds: Array.from(current) });
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  if (!reel) {
    return <div className="p-8 text-center text-slate-500">Loading your reel…</div>;
  }

  const includedKinds = new Set(reel.include_kinds || []);

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="public-reel-page">
      <div className="flex items-center gap-2 mb-2">
        <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">
          Public Reel
        </h1>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Publish your ⭐ favorites as a shareable portfolio page. Anyone with the link
        can view — no login needed. Turn public off anytime.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">⚠️ {error}</div>
      )}

      {/* Public toggle */}
      <div className={`p-4 rounded-2xl border-2 mb-6 transition-colors ${
        reel.is_public
          ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
      }`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              {reel.is_public ? '🟢 Your reel is live' : '⚪ Reel is private'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {reel.is_public
                ? 'Anyone with the link below can view your favorites.'
                : 'Turn on to generate a shareable link.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => patch({ is_public: !reel.is_public })}
            disabled={saving}
            data-testid="reel-toggle-public"
            className={`px-4 py-2 rounded-xl font-semibold text-sm shadow-lg transition-all ${
              reel.is_public
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-300'
                : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
            }`}
          >
            {reel.is_public ? 'Make private' : 'Publish reel'}
          </button>
        </div>

        {reel.is_public && (
          <div className="mt-4 flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={publicUrl}
              data-testid="reel-public-url"
              className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-mono"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="reel-copy-btn"
              className="px-3 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-semibold flex items-center gap-1"
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
            <a
              href={publicUrl}
              target="_blank" rel="noreferrer"
              data-testid="reel-open-btn"
              className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1 hover:bg-emerald-600"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
          </div>
        )}
        {reel.view_count > 0 && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <Eye className="w-3.5 h-3.5" /> {reel.view_count} view{reel.view_count === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mb-6">
        <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Reel settings</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium block mb-1">Handle (URL)</label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs">
                <span className="text-slate-400">/reel/</span>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
                  data-testid="reel-slug-input"
                  className="flex-1 bg-transparent outline-none font-mono"
                />
              </div>
              <button
                type="button"
                disabled={saving || !slugInput || slugInput === reel.slug}
                onClick={() => patch({ slug: slugInput })}
                data-testid="reel-slug-save"
                className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-40"
              >
                Save
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium block mb-1">Display name</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title !== reel.title && patch({ title })}
              data-testid="reel-title-input"
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm"
              maxLength={80}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium block mb-1">Tagline (optional)</label>
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              onBlur={() => tagline !== (reel.tagline || '') && patch({ tagline })}
              placeholder="e.g. AI Ad Creator · Chennai"
              data-testid="reel-tagline-input"
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm"
              maxLength={200}
            />
          </div>
        </div>
      </div>

      {/* Sections to include */}
      <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">Sections to include</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Pick what should appear on your public reel.</p>
        <div className="space-y-2">
          {KIND_OPTIONS.map(opt => {
            const on = includedKinds.has(opt.id);
            return (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer border-2 transition-colors ${
                  on ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleKind(opt.id)}
                  data-testid={`reel-kind-${opt.id}`}
                  className="mt-0.5 w-4 h-4 accent-emerald-500"
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{opt.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{opt.help}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PublicReelPage;
